import { useCallback, useEffect, useState } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { SignaturesIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import {
	useMobileNativeShare,
	useShareCtaPlacement,
	useSignatureTracking,
} from '#src/app/pages/petitionDetails/shared/hooks';
import { useMediaQuery } from '#src/app/shared/hooks/dom';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import { ActionButtonWrapper } from './ActionButtonWrapper';
import type { ActionPanelVariant } from './MobileActionButton';
import { MobileSignModal, usePreloadMobileSignModal } from './MobileSignModal';
import { useMobileModalContext } from './shared/useMobileModalContext';
import { useShowSignForm } from './shared/useShowSignForm';
import { SignCtaShareAffordance } from './SignCtaShareAffordance';

// Matches the `md:hidden` breakpoint that CSS-hides both `MobileActionButtonContainer` mounts on
// desktop viewports (containers/Main/index.tsx) — the tree still mounts and runs effects there,
// just invisibly. `useIsMobile()` (device-type sniffing, used by `hasShareAction`/`shareAction`'s
// native-share-capability check) classifies tablet UAs as mobile even at desktop-width viewports,
// so a tablet at >=768px could otherwise still record `$exposure` for a CTA it structurally never
// saw, contaminating every arm's denominator with unexposed visits. `initialValue: true` assumes
// desktop (blocking tracking) until the query resolves, rather than briefly assuming mobile.
const DESKTOP_BREAKPOINT_QUERY = '(min-width: 768px)';

type Props = {
	hideShareAffordance?: boolean;
	sticky: boolean;
	visible: boolean;
	variant: ActionPanelVariant;
};

/**
 * `shareCtaPlacementEnabled` already folds in `!hideShareAffordance` (see below), so it's not
 * re-checked here.
 */
function shouldTrackShareCtaCopyExperiment(
	sticky: boolean,
	hasShareAction: boolean,
	shareCtaPlacementEnabled: boolean,
	isDesktopViewport: boolean,
) {
	return !sticky && hasShareAction && shareCtaPlacementEnabled && !isDesktopViewport;
}

/**
 * Arm-independent (no variation term), recovered verbatim from `68cdbd63deb^`'s
 * `shouldTrackSharePlacementExperiment` — all four re-run arms share one availability predicate.
 * The `!hideShareAffordance` term matters: without it, the video-first population enters the
 * exposure denominator while receiving no affordance, which would drag every treatment arm's
 * share rate down with users who never saw the treatment. `!isDesktopViewport` matters for the
 * same reason: see `DESKTOP_BREAKPOINT_QUERY` above.
 */
function shouldTrackSharePlacementExperiment(
	sticky: boolean,
	hideShareAffordance: boolean,
	hasShareAction: boolean,
	isDesktopViewport: boolean,
): boolean {
	return !sticky && !hideShareAffordance && hasShareAction && !isDesktopViewport;
}

type UseShareCtaExposureTrackingParams = {
	sticky: boolean;
	hideShareAffordance: boolean;
	hasShareAction: boolean;
	shareCtaPlacementEnabled: boolean;
	isDesktopViewport: boolean;
	petitionId: string | undefined;
	copyExperiment: ReturnType<
		typeof usePetitionDetailsPageUserData
	>['data']['experiments']['petitionDetailsShareCtaCopyExperiment'];
	placementExperiment: ReturnType<
		typeof usePetitionDetailsPageUserData
	>['data']['experiments']['petitionDetailsShareCtaPlacementRerunExperiment'];
};

/**
 * Fired from stable effects on this component instance, not conditionally-mounted
 * <TrackExperimentExposure> components: `shareAction` resolves asynchronously (see
 * useMobileNativeShare, which queries on `sessionUuid`) and can flicker unavailable → available
 * after mount. A conditionally-mounted tracker would unmount/remount on that flicker, resetting
 * its per-mount dedup flag (useTrackOnce) and double-firing $exposure — this component instance
 * persists across the flicker, so calling the hook's returned function here keeps the dedup flag
 * stable instead. Both instances of MobileActionButtonContainer render this component; `!sticky`
 * in each `shouldTrack*` predicate (same guard as MobileSignButtonAutoOpen) is what prevents the
 * *other* double-fire vector, across the two sibling mounts. Each tracker uses its own
 * useTrackExperimentExposure instance and experiment object, so the copy and placement exposures
 * cannot double-fire or dedup against each other.
 */
function useShareCtaExposureTracking({
	sticky,
	hideShareAffordance,
	hasShareAction,
	shareCtaPlacementEnabled,
	isDesktopViewport,
	petitionId,
	copyExperiment,
	placementExperiment,
}: UseShareCtaExposureTrackingParams) {
	const trackShareCtaCopyExperiment = shouldTrackShareCtaCopyExperiment(
		sticky,
		hasShareAction,
		shareCtaPlacementEnabled,
		isDesktopViewport,
	);
	const trackSharePlacementExperiment = shouldTrackSharePlacementExperiment(
		sticky,
		hideShareAffordance,
		hasShareAction,
		isDesktopViewport,
	);
	// resetKeys is required, not optional: this component instance persists across an SPA
	// petition-to-petition navigation (no remount), and variation/experimentName are otherwise
	// unchanged, so without the petition id the new petition's legitimate exposure would be deduped
	// against the previous one. Same requirement as useTreatSignFormAddressRemovalExperiment on this page.
	const trackShareCtaCopyExposure = useTrackExperimentExposure(copyExperiment, { resetKeys: [petitionId] });
	const trackSharePlacementExposure = useTrackExperimentExposure(placementExperiment, { resetKeys: [petitionId] });

	useEffect(() => {
		if (trackShareCtaCopyExperiment) {
			void trackShareCtaCopyExposure();
		}
	}, [trackShareCtaCopyExperiment, trackShareCtaCopyExposure]);

	useEffect(() => {
		if (trackSharePlacementExperiment) {
			void trackSharePlacementExposure();
		}
	}, [trackSharePlacementExperiment, trackSharePlacementExposure]);
}

export const MobileSignButtonContainer: React.FC<Props> = ({
	hideShareAffordance = false,
	sticky,
	visible,
	variant,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const { trackMobileSignIntent } = useSignatureTracking();

	const context = useMobileModalContext();

	usePreloadMobileSignModal();

	const {
		data: {
			experiments: { petitionDetailsShareCtaCopyExperiment, petitionDetailsShareCtaPlacementRerunExperiment },
		},
	} = usePetitionDetailsPageUserData();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();

	// Render site, derived from the existing `sticky` prop — both instances render the affordance,
	// so this has no `sticky` gate; `sticky` only decides the tracking context value.
	const trackingContext = sticky ? 'petition_show_sign_cta_sticky' : 'petition_show_sign_cta_inline';

	// Evaluated in every arm, control included: this is the availability half of the render/exposure
	// predicate, and it must be arm-independent so the compared populations are identical.
	const shareAction = useMobileNativeShare(trackingContext);
	// Hooks must be called unconditionally: resolve the placement first, then apply
	// `hideShareAffordance` afterward, matching `68cdbd63deb^`'s
	// `hideShareAffordance ? null : getSharePlacement(...)` without a conditional hook call.
	const resolvedPlacement = useShareCtaPlacement();
	const placement = hideShareAffordance ? null : resolvedPlacement;
	const shareCtaPlacementEnabled = !!placement;
	const isDesktopViewport = useMediaQuery(DESKTOP_BREAKPOINT_QUERY, true);

	useShareCtaExposureTracking({
		sticky,
		hideShareAffordance,
		hasShareAction: !!shareAction,
		shareCtaPlacementEnabled,
		isDesktopViewport,
		petitionId,
		copyExperiment: petitionDetailsShareCtaCopyExperiment,
		placementExperiment: petitionDetailsShareCtaPlacementRerunExperiment,
	});

	const openModal = useCallback(() => {
		trackMobileSignIntent();
		setIsOpen(true);
	}, [trackMobileSignIntent]);

	const openModalWithoutTracking = useCallback(() => {
		setIsOpen(true);
	}, []);

	const signButton = (
		<Button
			variant="primary"
			onClick={openModal}
			leadingIcon={<SignaturesIcon size="small" />}
			data-qa="mobile-sign-button"
			data-testid="mobile-sign-button"
			data-gtm="ga4-sign-button"
			size="large"
			className={placement === 'beside' ? 'min-w-0 flex-1' : 'w-full max-w-none'}
		>
			<div
				className={
					placement === 'beside'
						? 'flex min-w-0 place-content-center place-items-center gap-3'
						: 'flex place-content-center place-items-center gap-3'
				}
			>
				{placement === 'beside' ? (
					// `truncate` needs to sit on the text-containing element itself, not the flex
					// container around it — a flex container's own overflow/white-space rules don't
					// apply ellipsis truncation to its (anonymous) flex-item children.
					<span className="min-w-0 truncate">
						<Translate value="corgi.petition_gamma.petition_sign.action" />
					</span>
				) : (
					<Translate value="corgi.petition_gamma.petition_sign.action" />
				)}
			</div>
		</Button>
	);

	return (
		<>
			{/* we're looking at breakpoints to decide whether to open the modal, which cannot be done server-side */}
			{/* Only auto-open on the non-sticky instance to avoid opening modal twice (both sticky and non-sticky render this component) */}
			{!sticky && (
				<ClientRender>
					<MobileSignButtonAutoOpen onOpen={openModalWithoutTracking} />
				</ClientRender>
			)}
			{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
			<ActionButtonWrapper visible={visible} sticky={sticky}>
				{placement ? (
					<div className={placement === 'beside' ? 'flex items-center gap-2' : 'flex flex-col gap-2'}>
						{signButton}
						<SignCtaShareAffordance
							placement={placement}
							shareAction={shareAction}
							variation={petitionDetailsShareCtaCopyExperiment.variation}
						/>
					</div>
				) : (
					signButton
				)}
			</ActionButtonWrapper>

			<MobileSignModal context={context} isOpen={isOpen} setIsOpen={setIsOpen} variant={variant} />
		</>
	);
};
function MobileSignButtonAutoOpen({ onOpen }: { onOpen: () => void }) {
	const showSignForm = useShowSignForm();

	useEffect(() => {
		if (showSignForm) {
			/**
			 * There are some updates that happen right before showing the modal (such as populating the form fields)
			 * that need to complete first so that the modal opens with the correct data.
			 * Using setTimeout to defer the onOpen call to the next event loop iteration and avoid a race condition.
			 */
			setTimeout(() => {
				onOpen();
			}, 0);
		}
		// disabling dependency check to avoid attempting to open the modal multiple times
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}
